import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CompletedNoSua from '../views/TuitionProcessNoSua/steps/Completed/CompletedNoSua';
import Documentation from '../views/TuitionProcessNoSua/steps/Documentation';
import PaymentStep from '../views/TuitionProcessNoSua/steps/Payment/index';
import StudentFileNoSuaStep from '../views/TuitionProcessNoSua/steps/StudentFile/index';
import Sustainer from '../views/TuitionProcessNoSua/steps/SustainerFile';
import Tariff from '../views/TuitionProcessNoSua/steps/Tariff';
import SelectCareer from '../views/TuitionProcessNoSua/steps/selectCareer/SelectCareer';
import { useStepState } from './useStepState';

export const useTuitionProcessNoSuaSteps = ({
  hasOnlinePayment = false,
}: {
  hasOnlinePayment?: boolean;
}) => {
  const { t } = useTranslation();
  const prefix = 'tuitionProcessNoSua';
  const { currentStep } = useStepState();

  /**
   * Contiene los pasos del proceso de matrícula SIN SUA
   */
  const stepsTuitionProcess = useMemo(() => {
    return [
      {
        id: 'study-plan-selection',
        enabled: true,
        name: t(`${prefix}.steps.selectCareer`),
        component: SelectCareer,
      },
      {
        id: 'student-file',
        enabled: true,
        name: t(`${prefix}.steps.studentFile`),
        component: StudentFileNoSuaStep,
      },
      {
        id: 'sustainer',
        enabled: true,
        name: t(`${prefix}.steps.tuitionPayer`),
        component: Sustainer,
      },
      {
        id: 'tariff',
        enabled: true,
        name: t(`${prefix}.steps.tariffQuotas`),
        component: Tariff,
      },
      {
        id: 'sign-documentation',
        enabled: true,
        name: t(`${prefix}.steps.sign`),
        component: Documentation,
      },
      {
        id: 'payment',
        enabled: hasOnlinePayment,
        name: t(`${prefix}.steps.payment`),
        component: PaymentStep,
      },
      {
        id: 'completed',
        enabled: true,
        name: t(`${prefix}.steps.completed`),
        component: CompletedNoSua,
      },
    ].filter((step) => step.enabled);
  }, [t, hasOnlinePayment]);

  /**
   * Contiene dos arrays con las partes de los pasos
   * (Un array con los nombres de los pasos y
   * un array con los componentes asociados a cada paso)
   */
  const availableParts = useMemo(() => {
    const namesSteps: string[] = [];
    const componentsSteps: (React.FunctionComponent | undefined)[] = [];
    stepsTuitionProcess.forEach((step) => {
      namesSteps.push(step.name);
      componentsSteps.push(step.component);
    });
    return { namesSteps: namesSteps.slice(0, -1), componentsSteps };
  }, [stepsTuitionProcess]);

  const getIndexStep = useCallback(
    (id: string) => {
      return stepsTuitionProcess.findIndex((step) => step.id === id);
    },
    [stepsTuitionProcess],
  );

  /**
   * El paso actual en base a la variable currentStep
   */
  const CurrentStepComponent = useMemo(() => {
    return availableParts.componentsSteps[currentStep];
  }, [currentStep, availableParts]);

  return {
    stepsTuitionProcess,
    ...availableParts,
    CurrentStepComponent,
    getIndexStep,
  };
};
